import React, { useEffect, useRef, useState } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapPin, Search, Crosshair, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common/FormSection";
import { DEFAULT_CENTER } from "@/pages/fleet/constants";
import { useMapboxToken } from "@/hooks/useMapboxToken";

const SHORT_ADDRESS_RE = /^[A-Za-z]{4}\d{4}$/;

/**
 * Structured home-address capture with a Mapbox geocode + draggable pin.
 * Value is an address object: { line1, district, street, building_no,
 * short_address, landmark, directions, city, latitude, longitude }.
 * Stored as-is on the patient record; the Fleet map reads latitude/longitude.
 */
export function AddressLocationPicker({ value, onChange, testidPrefix = "addr" }) {
  const MAPBOX_TOKEN = useMapboxToken();
  const v = (value && typeof value === "object") ? value : (value ? { line1: value } : {});
  const set = (patch) => onChange({ ...v, ...patch });
  const [viewState, setViewState] = useState({
    latitude: v.latitude || DEFAULT_CENTER.latitude,
    longitude: v.longitude || DEFAULT_CENTER.longitude,
    zoom: v.latitude ? 14 : DEFAULT_CENTER.zoom,
  });
  const [geocoding, setGeocoding] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    if (v.latitude && v.longitude) {
      setViewState((s) => ({ ...s, latitude: v.latitude, longitude: v.longitude }));
    }
  }, [v.latitude, v.longitude]);

  const [suggestions, setSuggestions] = useState([]);
  const [openSug, setOpenSug] = useState(false);
  const searchTimer = useRef(null);

  function ctxText(feat, prefix) {
    const c = (feat.context || []).find((x) => (x.id || "").startsWith(prefix));
    return c ? c.text : null;
  }

  async function fetchSuggestions(text) {
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(text)}.json?access_token=${MAPBOX_TOKEN}&country=SA&language=en&autocomplete=true&limit=6&types=address,poi,neighborhood,locality,place`;
      const res = await fetch(url);
      const data = await res.json();
      setSuggestions(data.features || []);
      setOpenSug((data.features || []).length > 0);
    } catch (_e) { setSuggestions([]); setOpenSug(false); }
  }

  function onLine1Change(text) {
    set({ line1: text });
    clearTimeout(searchTimer.current);
    if (text.trim().length < 3) { setSuggestions([]); setOpenSug(false); return; }
    searchTimer.current = setTimeout(() => fetchSuggestions(text), 300);
  }

  function pickSuggestion(feat) {
    const [lng, lat] = feat.center;
    const isAddr = (feat.place_type || []).includes("address");
    const line1 = isAddr ? [feat.address, feat.text].filter(Boolean).join(" ") : feat.text;
    set({
      line1: line1 || feat.place_name,
      district: ctxText(feat, "neighborhood") || ctxText(feat, "locality") || v.district || "",
      city: ctxText(feat, "place") || v.city || "",
      latitude: lat, longitude: lng,
    });
    setViewState((s) => ({ ...s, latitude: lat, longitude: lng, zoom: 15 }));
    setOpenSug(false);
    setSuggestions([]);
  }


  async function reverseGeocode(lat, lng) {
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&country=SA&language=en&types=address,neighborhood,locality,place,postcode`;
      const res = await fetch(url);
      const data = await res.json();
      const feats = data.features || [];
      if (!feats.length) return null;
      const pick = (type) => {
        for (const f of feats) {
          if ((f.place_type || []).includes(type)) return f.text;
          const ctx = (f.context || []).find((c) => (c.id || "").startsWith(type));
          if (ctx) return ctx.text;
        }
        return null;
      };
      const addrFeat = feats.find((f) => (f.place_type || []).includes("address"));
      const line1 = addrFeat ? [addrFeat.address, addrFeat.text].filter(Boolean).join(" ") : (feats[0].text || "");
      return {
        line1: line1 || "",
        district: pick("neighborhood") || pick("locality") || "",
        city: pick("place") || "",
      };
    } catch (_e) { return null; }
  }

  async function setPin(lat, lng) {
    const patch = { latitude: lat, longitude: lng };
    const rev = await reverseGeocode(lat, lng);
    if (rev) {
      // Auto-fill address fields from the pin location.
      if (rev.line1) patch.line1 = rev.line1;
      if (rev.district) patch.district = rev.district;
      if (rev.city) patch.city = rev.city;
    }
    set(patch);
  }

  function useMyLocation() {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setViewState((s) => ({ ...s, latitude, longitude, zoom: 16 }));
        await setPin(latitude, longitude);
        setLocating(false);
        toast.success("Located you — drag the pin to fine-tune");
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === err.PERMISSION_DENIED
          ? "Location permission denied. Enable it in your browser, or set the pin manually."
          : "Couldn't get your location. Set the pin manually.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  async function geocode() {
    const query = [v.building_no, v.street || v.line1, v.district, v.city, "Saudi Arabia"]
      .filter(Boolean).join(", ");
    if (!query.trim()) { toast.error("Enter an address first (street / district / city)"); return; }
    setGeocoding(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=SA&limit=1`;
      const res = await fetch(url);
      const data = await res.json();
      const feat = (data.features || [])[0];
      if (!feat) { toast.error("Couldn't locate that address — drag the pin to set it manually"); return; }
      const [lng, lat] = feat.center;
      set({ latitude: lat, longitude: lng });
      setViewState((s) => ({ ...s, latitude: lat, longitude: lng, zoom: 15 }));
      toast.success("Location found — drag the pin to fine-tune");
    } catch (_e) {
      toast.error("Geocoding failed — drag the pin to set the location");
    } finally { setGeocoding(false); }
  }

  const shortInvalid = v.short_address && !SHORT_ADDRESS_RE.test(v.short_address);

  if (!MAPBOX_TOKEN) {
    return <div className="text-xs text-[#6B7280]">Loading map…</div>;
  }

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Street / Address line (search)">
          <div className="relative">
            <Input
              value={v.line1 || ""}
              onChange={(e) => onLine1Change(e.target.value)}
              onFocus={() => suggestions.length && setOpenSug(true)}
              onBlur={() => setTimeout(() => setOpenSug(false), 150)}
              placeholder="Start typing an address or place…"
              autoComplete="off"
              data-testid={`${testidPrefix}-line1`}
            />
            {openSug && suggestions.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-60 overflow-auto" data-testid={`${testidPrefix}-suggestions`}>
                {suggestions.map((f) => (
                  <button
                    type="button"
                    key={f.id}
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(f); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#F1F5F9] flex items-start gap-2"
                    data-testid={`${testidPrefix}-suggestion`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-[#0B7E8C] mt-0.5 shrink-0" />
                    <span><span className="font-medium text-[#1F2937]">{f.text}</span><span className="text-[#6B7280]">{f.place_name ? ` — ${f.place_name}` : ""}</span></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </FormField>
        <FormField label="District / Neighborhood">
          <Input value={v.district || ""} onChange={(e) => set({ district: e.target.value })} placeholder="e.g. Al Olaya" data-testid={`${testidPrefix}-district`} />
        </FormField>
        <FormField label="Building / Villa no.">
          <Input value={v.building_no || ""} onChange={(e) => set({ building_no: e.target.value })} placeholder="e.g. 2929" data-testid={`${testidPrefix}-building`} />
        </FormField>
        <FormField label="City">
          <Input value={v.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder="e.g. Riyadh" data-testid={`${testidPrefix}-city`} />
        </FormField>
        <FormField label="National Address (Short Address)">
          <Input value={v.short_address || ""} onChange={(e) => set({ short_address: e.target.value.toUpperCase() })} placeholder="e.g. RGNB2929" maxLength={8} data-testid={`${testidPrefix}-short`} />
          {shortInvalid && <div className="text-[11px] text-[#B45309] mt-1">Format: 4 letters + 4 digits (e.g. RGNB2929)</div>}
        </FormField>
        <FormField label="Nearest landmark">
          <Input value={v.landmark || ""} onChange={(e) => set({ landmark: e.target.value })} placeholder="e.g. Near Panorama Mall" data-testid={`${testidPrefix}-landmark`} />
        </FormField>
      </div>
      <FormField label="Additional directions">
        <Input value={v.directions || ""} onChange={(e) => set({ directions: e.target.value })} placeholder="Gate code, floor, access notes…" data-testid={`${testidPrefix}-directions`} />
      </FormField>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={geocode} disabled={geocoding} className="border-[#0B7E8C] text-[#0B7E8C]" data-testid={`${testidPrefix}-geocode`}>
            <Search className="w-3.5 h-3.5 mr-1.5" /> {geocoding ? "Locating…" : "Find on map"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={useMyLocation} disabled={locating} className="border-[#0B7E8C] text-[#0B7E8C]" data-testid={`${testidPrefix}-mylocation`}>
            <LocateFixed className="w-3.5 h-3.5 mr-1.5" /> {locating ? "Locating you…" : "Use my current location"}
          </Button>
        </div>
        <span className="text-[11px] text-[#6B7280] inline-flex items-center gap-1">
          <Crosshair className="w-3 h-3" />
          {v.latitude && v.longitude ? `${v.latitude.toFixed(5)}, ${v.longitude.toFixed(5)}` : "No pin set — drag the marker"}
        </span>
      </div>

      <div className="rounded-lg overflow-hidden border border-[#E5E7EB]" style={{ height: 280 }}>
        <Map
          {...viewState}
          onMove={(e) => setViewState(e.viewState)}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          style={{ width: "100%", height: "100%" }}
          onClick={(e) => setPin(e.lngLat.lat, e.lngLat.lng)}
        >
          <NavigationControl position="top-right" />
          {v.latitude && v.longitude && (
            <Marker
              longitude={v.longitude}
              latitude={v.latitude}
              anchor="bottom"
              draggable
              onDragEnd={(e) => setPin(e.lngLat.lat, e.lngLat.lng)}
            >
              <MapPin className="w-7 h-7 text-[#EF4444] drop-shadow" fill="#EF4444" />
            </Marker>
          )}
        </Map>
      </div>
      <div className="text-[11px] text-[#94A3B8]">Click the map or drag the pin to set the location — the address fields auto-fill from the pin.</div>
    </div>
  );
}

export default AddressLocationPicker;
